import { AxiosResponse } from 'axios';
import {
  MERCADO_PAGO_ENDPOINT,
  MY_MEMBERSHIP_FEES_PAGE_ROUTE, PAYMENT_METHOD_ID_MERCADO_PAGO, PAYWAY_ENDPOINT,
  POST_PAYMENT_ENDPOINT, POST_PAYMENT_UPLOAD_RECEIPT_ENDPOINT
} from '../constants';
import type { MercadoPagoPreferenceResponse } from '../models/mercado-pago-preference-response';
import {
  MercadoPagoPaymentRequest, PaymentDetail,
  ProductsPayment
} from '../models/product-to-pay';
import type { MercadoPagoPreferenceItem } from '../models/mercado-pago-preference-item';
import type { MercadoPagoPreference } from '../models/mercado-pago-preference';
import axiosPrivate from '../models/axios/axios-private';
import {Payment} from "../models/payment";
import {HttpRequestResponse} from "../models/web/http-request-response";
import {AxiosError} from "axios";
import {getEndpointResponse} from "../utils/endpoint-response";
import {PostPaymentRequest} from "../models/web/post-payment-request";

export const makePostPaymentsCall = async(payment: PostPaymentRequest): Promise<HttpRequestResponse<Payment>> => {
  const response: AxiosResponse | AxiosError = await axiosPrivate
    .post<Payment>(POST_PAYMENT_ENDPOINT, payment)
    .catch((err: AxiosError): AxiosError => err);

  return getEndpointResponse(response);
}

export const makePostPaymentsUploadReceiptCall = async(paymentId: string, file: File): Promise<HttpRequestResponse<Payment>> => {
  const response: AxiosResponse | AxiosError = await axiosPrivate
    .postForm<Payment>(POST_PAYMENT_UPLOAD_RECEIPT_ENDPOINT.replace('{PaymentId}', paymentId), {
      file
    })
    .catch((err: AxiosError): AxiosError => err);

  return getEndpointResponse(response);
}

export const makePostPaywayPaymentCall = async(payment: PostPaymentRequest, token: string): Promise<HttpRequestResponse<Payment>> => {
  const request = {
    Payment: payment,
    Token: token
  }

  const response: AxiosResponse | AxiosError = await axiosPrivate
    .post(PAYWAY_ENDPOINT, request)
    .catch((err: AxiosError): AxiosError => err);

  return getEndpointResponse(response);
}

export const getMercadoPagoPreferenceId = async (
  mercadoPagoPaymentData: ProductsPayment
): Promise<string | null> => {
  if (mercadoPagoPaymentData.products.length === 0) {
    return null;
  }

  const preferenceItems: MercadoPagoPreferenceItem[] = [];
  for (const membershipFee of mercadoPagoPaymentData.products) {
    const preferenceItem: MercadoPagoPreferenceItem = {
      id: membershipFee.productId,
      title: `C.A. Pacífico - ${membershipFee.product.description}`,
      description: membershipFee.product.name,
      unit_price: Number(membershipFee.remainingPrice),
      quantity: 1
    };
    preferenceItems.push(preferenceItem);
  }

  const preference: MercadoPagoPreference = {
    items: preferenceItems,
    back_urls: {
      success: `${window.location.origin}${MY_MEMBERSHIP_FEES_PAGE_ROUTE}`,
      pending: `${window.location.origin}${MY_MEMBERSHIP_FEES_PAGE_ROUTE}`
    }
  };

  const mercadoPagoPaymentRequest: MercadoPagoPaymentRequest = {
    payment: {
      memberId: mercadoPagoPaymentData.memberId,
      paymentDetails: mercadoPagoPaymentData.products.map(
        (p): PaymentDetail => {
          return {
            productId: p.productId,
            price: Number(p.remainingPrice),
            discriminator: p.product.discriminator
          };
        }
      ),
      methodId: PAYMENT_METHOD_ID_MERCADO_PAGO,
      comments: 'Abonado por MercadoPago a través de la sede virtual'
    },
    mercadoPagoPreference: preference
  };

  const response: AxiosResponse | AxiosError = await axiosPrivate
    .post(MERCADO_PAGO_ENDPOINT, mercadoPagoPaymentRequest)
    .catch((err: AxiosError): AxiosError => err);

  const endpointResponse =
    getEndpointResponse<MercadoPagoPreferenceResponse>(response);

  return endpointResponse.response?.id ?? null;
};
